import {compact, naturalSort} from '@benchling/js-utils';
import {NodeChange, EdgeChange} from 'reactflow';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  createPlaceholderNode,
  createRouterNode,
  createStopNode,
  createTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import {RouterConfig} from 'processes/modals/router-config/RouterConfigConstants';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ClientSchemaMapperConfig} from '../FlowchartConfigConstants';
import {getLogicNodeMetadata} from './logicNodeMetadata';
import {
  CustomMappingData,
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  FLOWCHART_TASK_SECONDARY_INFO,
  FlowchartStaticEdgeData,
  ProcessFlowchartNodeType,
  ProcessTaskDefinitionVersionGraphQL,
  TransitionSource,
} from './processFlowchartConfigEditorConstants';
import {convertTaskDefinitionVersionToSpec} from './processFlowchartConfigEditorNetworkUtils';
import {
  RouterStaticNodeData,
  EndStaticNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  StaticDataToNode,
  TaskSchemaStaticNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  GetAddNodeOptionsForRightHandle,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata, FlowchartConfigMetadataOptions} from './redux/reduceFlowchartConfig';
import {getCustomMappingDataForDataFlowEdge} from './types/transition/transitionAdapter';
import {Transition} from './types/transition/TransitionTypes';
import {LiveStateChanges} from './useFlowchartState/BaseFlowchartConfigStateManager';

export interface FlowchartOptions {
  onNodeHeaderClick?: (nodeId: string) => void;
  onNodeDelete?: (nodeId: string) => void;
  onFieldClick: (nodeId: string, fieldId: string, rowId: string) => void;
  onUpdateNodeData: (
    nodeChangeGenerator: (flowchartConfig: FlowchartConfigWithMetadata) => Array<FlowchartNodeChange>,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
  refetchFlowchartConfig?: () => void;
  getAddNodeOptionsForRightHandle?: GetAddNodeOptionsForRightHandle;
  validationErrors?: Array<ProcessFlowchartValidationError>;
  existingNodeNames?: Array<string>;
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
}

export enum FlowchartNodeChangeType {
  ADD = 'add',
  REPLACE = 'replace',
  POSITION = 'position',
  SELECT = 'select',
  SELECT_ROW = 'select_row',
  RESET = 'reset',
  DIMENSIONS = 'dimensions',
  REMOVE = 'remove',
}

/**
 * Represents a "replace" change on a node. This will change the "data" attribute on the node.
 *
 * This change event is supported in ReactFlow 12.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ReplaceNodeChange<TNodeData = any> {
  id: string;
  data: TNodeData;
  type: FlowchartNodeChangeType.REPLACE;
}

/**
 * Represents selecting a specific row in a section on a node
 */
interface SelectRowNodeChange {
  id: string;
  rowId: string;
  selected: boolean;
  type: FlowchartNodeChangeType.SELECT_ROW;
}

/**
 * Represents a change that can be made to a node.
 *
 * Reactflow NodeChange types are described here: https://v11.reactflow.dev/api-reference/types/node-change
 */
export type FlowchartNodeChange = NodeChange | ReplaceNodeChange | SelectRowNodeChange;

export enum FlowchartEdgeChangeType {
  ADD = 'add',
  REMOVE = 'remove',
  SELECT = 'select',
  REPLACE = 'replace',
  LABEL = 'label',
  RESET = 'reset',
}

/**
 * Represents an update to the label of an edge.
 */
interface EdgeLabelChange {
  type: FlowchartEdgeChangeType.LABEL;
  id: string;
  label: string;
}

/**
 * Edge removal that carries PTDV IDs of a node that has already been deleted from
 * static data. This lets downstream transition cleanup resolve the PTDV pair even
 * though the node is no longer in nodesStaticData.
 */
export interface DeletedNodeContext {
  deletedSourceNodePTDVId?: string;
  deletedTargetNodePTDVId?: string;
  /**
   * When true, the deleted node was a router. The PTDV IDs in this context represent
   * the effective PTDVs that flowed *through* the router (not the router's own, since
   * routers have none). This tells buildNodeToPTDVIdMap to skip mapping the router's
   * node ID so the BFS can still traverse through it in the remaining edge topology.
   */
  deletedNodeWasRouter?: boolean;
}

export interface EdgeRemoveChangeWithDeletedNodeContext {
  type: 'remove';
  id: string;
  deletedNodeContext?: DeletedNodeContext;
}

/**
 * Represents a change that can be made to an edge.
 *
 * Reactflow EdgeChange types are described here: https://v11.reactflow.dev/api-reference/types/edge-change
 */
export type FlowchartEdgeChange =
  | Exclude<EdgeChange, {type: 'remove'}>
  | EdgeLabelChange
  | EdgeRemoveChangeWithDeletedNodeContext;

interface TransitionAdd {
  type: 'add';
  transition: Transition;
}

interface TransitionRemove {
  type: 'remove';
  id: string;
}

interface TransitionUpdate {
  type: 'update';
  id: string;
  changes: {
    transitionSource?: {sourcePTDVId: string; value: TransitionSource};
    customMapping?: {fieldPairKey: string; value: CustomMappingData | null};
    schemaMapper?: ClientSchemaMapperConfig;
    isPlanned?: boolean;
  };
}

/**
 * Represents a change to a transition between PTDVs.
 */
export type TransitionChange = TransitionAdd | TransitionRemove | TransitionUpdate;

export enum FlowchartMetadataOptionsChangeType {
  UPDATE = 'update',
}

interface FlowchartMetadataOptionsUpdate {
  type: FlowchartMetadataOptionsChangeType.UPDATE;
  options: Partial<FlowchartConfigMetadataOptions>;
}

export type FlowchartMetadataOptionsChange = FlowchartMetadataOptionsUpdate;

interface NodePosition {
  x: number;
  y: number;
}

export function createStaticNodeDataFromRouterConfig(
  nodeClientId: string,
  routerConfig: RouterConfig
): RouterStaticNodeData {
  const metadata = getLogicNodeMetadata(ProcessFlowchartNodeType.ROUTER_NODE);
  return {
    id: nodeClientId,
    nodeType: ProcessFlowchartNodeType.ROUTER_NODE,
    nodeDetailsType: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    nodeName: routerConfig.name,
    nodeIcon: metadata.icon,
    routerConfig,
  };
}

export function createEndNodeStaticData(nodeClientId: string, nodeName: string): EndStaticNodeData {
  const metadata = getLogicNodeMetadata(ProcessFlowchartNodeType.STOP_NODE);
  return {
    id: nodeClientId,
    nodeType: ProcessFlowchartNodeType.STOP_NODE,
    nodeDetailsType: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
    nodeName,
    nodeIcon: metadata.icon,
  };
}

interface CreateStaticNodeDataFromTaskDefinitionVersionSpecParams {
  nodeClientId: string;
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema;
  taskSchemaId: string;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  existingNodeNames?: Array<string>;
  isEditingNodeName?: boolean;
}

/**
 * Generate data for node from TaskDefinitionVersionSpecWithTaskSchema, using task schema node adapter to customize
 */
export function createStaticNodeDataFromTaskDefinitionVersionSpec(
  params: CreateStaticNodeDataFromTaskDefinitionVersionSpecParams
): TaskSchemaStaticNodeData {
  const {
    nodeClientId,
    taskDefinitionVersionSpec,
    taskSchemaId,
    nodeDetailsType,
    existingNodeNames,
    isEditingNodeName,
  } = params;
  const taskSchema = taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema;
  const isFlowchartTask = isFlowchartTaskSchema({executionType: taskSchema.executionType});
  const isFlowchartOutputNode =
    isFlowchartTask && nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(taskSchema.executionType);

  return {
    id: nodeClientId,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeDetailsType,
    nodeName: adapter.getNodeName(taskDefinitionVersionSpec, isFlowchartOutputNode, existingNodeNames),
    nodeIcon: adapter.getNodeIcon(taskDefinitionVersionSpec, isFlowchartOutputNode) ?? undefined,
    taskDefinitionVersionSpec,
    taskSchemaId,
    nodeSecondaryInfo: adapter.getNodeSubtitle(taskDefinitionVersionSpec, isFlowchartOutputNode) ?? undefined,
    sections: adapter.createSections(taskDefinitionVersionSpec, nodeDetailsType),
    hideSectionSummaries: true,
    isEditingNodeName,
  };
}

interface CreateStaticNodeDataFromTaskDefinitionVersionParams {
  nodeClientId: string;
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  existingNodeNames?: Array<string>;
}
/**
 * Generate data for node, using task schema node adapter to customize
 */
export function createStaticNodeDataFromTaskDefinitionVersion(
  params: CreateStaticNodeDataFromTaskDefinitionVersionParams
): TaskSchemaStaticNodeData {
  const {nodeClientId, taskDefinitionVersion, nodeDetailsType, allTaskSchemas, existingNodeNames} = params;
  const {processTaskSchema} = taskDefinitionVersion;
  assert(!isInaccessible(processTaskSchema), 'processTaskSchema is required');

  const fullTaskSchema = allTaskSchemas.find((schema) => schema.id === processTaskSchema.id);
  assert(fullTaskSchema != null, `Task schema not found in allTaskSchemas: ${processTaskSchema.id}`);

  const taskDefinitionVersionSpec = convertTaskDefinitionVersionToSpec(taskDefinitionVersion, fullTaskSchema);
  return {
    ...createStaticNodeDataFromTaskDefinitionVersionSpec({
      nodeClientId,
      taskDefinitionVersionSpec,
      taskSchemaId: processTaskSchema.id,
      nodeDetailsType,
      existingNodeNames,
    }),
    isArchived: isArchived(fullTaskSchema),
  };
}

export function createInteractiveNodeFromStaticData<T extends StaticFlowchartNodeData>(
  staticData: T,
  position: NodePosition,
  options: FlowchartOptions
): StaticDataToNode<T> {
  switch (staticData.nodeType) {
    case ProcessFlowchartNodeType.TASK_SCHEMA_NODE:
      return createTaskNode(staticData, position, options) as StaticDataToNode<T>;
    case ProcessFlowchartNodeType.ROUTER_NODE:
      return createRouterNode(staticData, position, options) as StaticDataToNode<T>;
    case ProcessFlowchartNodeType.STOP_NODE:
      return createStopNode(staticData, position, options) as StaticDataToNode<T>;
    case ProcessFlowchartNodeType.PLACEHOLDER_NODE:
      return createPlaceholderNode(staticData, position, options) as StaticDataToNode<T>;
    default: {
      assert(false, `Unknown node type: ${assertNever(staticData)}`);
    }
  }
}

/**
 * Gets the schema infos for available task nodes that can be added
 * given a flowchart task schema and list of nodes already present in the flowchart.
 *
 * Need to use schema info format because the root task schema is repeated for the flowchart root and output
 * nodes, and we need to differentiate between them.
 */
export function getAvailableTaskSchemaInfos(
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>,
  nodes: Array<TaskSchemaStaticNodeData>,
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  allowedExecutionTypes: Array<ProcessExecutionType>
): Array<SchemaInfo> {
  let schemaInfos: Array<SchemaInfo> = compact(
    allTaskSchemas.map((schema) => {
      if (
        !allowedExecutionTypes.includes(schema.executionType) ||
        schema.executionType === ProcessExecutionType.FLOWCHART // Don't show other flowchart task schemas in the sidepanel
      ) {
        return null;
      }
      return {
        taskSchema: schema,
        itemType: ModelTypeConstants.PROCESS_TASK,
      };
    })
  );

  if (
    flowchartTaskSchema.outputSchema != null &&
    !nodes.some((node) => node.nodeSecondaryInfo === FLOWCHART_OUTPUT_SECONDARY_INFO)
  ) {
    schemaInfos = [
      {
        taskSchema: flowchartTaskSchema,
        itemType: ModelTypeConstants.PROCESS_OUTPUT,
      },
      ...schemaInfos,
    ];
  }
  if (!nodes.some((node) => node.nodeSecondaryInfo === FLOWCHART_TASK_SECONDARY_INFO)) {
    schemaInfos = [
      {
        taskSchema: flowchartTaskSchema,
        itemType: ModelTypeConstants.PROCESS_TASK,
      },
      ...schemaInfos,
    ];
  }
  return schemaInfos;
}

export function getSingletonTaskSchemaNamesForFlowchartConfig(
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator,
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
): Array<ProcessSingletonTaskSchemaName> {
  const names = compact(
    flowchartEditorConfigurator.allowedNodeExecutionTypes.map((executionType) => {
      const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
      return adapter.getSingletonTaskSchemaName(flowchartTaskSchema);
    })
  );
  return naturalSort([...new Set(names)]) as Array<ProcessSingletonTaskSchemaName>;
}

/**
 * Check if a data flow edge has lookup configured using the transitions structure.
 */
export function isLookupConfiguredForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): boolean {
  const customMapping = getCustomMappingDataForDataFlowEdge(edge, transitions, nodes);
  return customMapping?.lookupConfig != null;
}

/**
 * Check if a data flow edge has lookup configured by edge ID.
 */
export function isLookupConfiguredForEdgeFromTransitions(
  edgeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): boolean {
  const edge = edges.find((e) => e.id === edgeId);
  return edge != null ? isLookupConfiguredForDataFlowEdge(edge, transitions, nodes) : false;
}

export function getLockedStateAndErrorMessage(
  locked: boolean,
  laBlockingReason: string | null,
  lockErrorMessage: string | undefined,
  liveStateChanges: LiveStateChanges
): {computedLocked: boolean; computedLockErrorMessage: string | undefined} {
  const defaultLockErrorMessage = 'Editing is disabled. This flowchart is locked.';

  if (locked) {
    // If the flowchart is locked by the parent of the presentational componenent, use the parent's lock state and message.
    return {computedLocked: locked, computedLockErrorMessage: lockErrorMessage ?? defaultLockErrorMessage};
  }
  if (laBlockingReason != null) {
    return {computedLocked: true, computedLockErrorMessage: laBlockingReason};
  } else if (liveStateChanges.isAutosaverBlockedState[0]) {
    return {
      computedLocked: true,
      computedLockErrorMessage:
        'This flowchart has encountered an error while saving. Please refresh the page and try again.',
    };
  }
  return {computedLocked: false, computedLockErrorMessage: defaultLockErrorMessage};
}

/**
 * Safely ensures that a FlowchartConfigWithMetadata has a non-null rootTaskSchema.
 * Throws an assertion error if rootTaskSchema is null, otherwise returns the typed version.
 */
export function getFlowchartConfigSafely(
  flowchartConfig: FlowchartConfigWithMetadata
): FlowchartConfigWithMetadataAndRootTaskSchema {
  assert(flowchartConfig.metadata.rootTaskSchema != null, 'Root task schema is required');
  return flowchartConfig as FlowchartConfigWithMetadataAndRootTaskSchema;
}

export function getRootAdapter(
  plugin: ProcessesPluginBase
): ProcessFlowchartAdapter<unknown, unknown, unknown> | undefined {
  return plugin.nodeAdapters.find((adapter) => adapter instanceof ProcessFlowchartAdapter) as
    | ProcessFlowchartAdapter<unknown, unknown, unknown>
    | undefined;
}

export function findNodeIdsByPTDVClientId(
  nodes: Array<StaticFlowchartNodeData>,
  ptdvClientId: string
): Array<string> {
  return nodes
    .filter(
      (node) => isStaticTaskSchemaNodeData(node) && node.taskDefinitionVersionSpec.clientId === ptdvClientId
    )
    .map((node) => node.id);
}
